import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1,
  Heading2, AlignLeft, AlignCenter, AlignRight, AlignJustify, Image as ImageIcon,
  Video, Link as LinkIcon, Code2, Quote, Eye, EyeOff,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { lmsApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * RichContentEditor — Tiptap with image upload, YouTube/Vimeo embeds,
 * links, raw HTML insertion, and the standard formatting toolbar.
 *
 * Used by both the Knowledge Base article editor and the Forum thread/post
 * composers.
 */
export default function RichContentEditor({
  value,
  onChange,
  placeholder = 'Write something amazing…',
  minHeight = 200,
  enableMedia = true,
}) {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [showHtmlDialog, setShowHtmlDialog] = useState(false);
  const [rawHtml, setRawHtml] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ inline: false, allowBase64: true,
        HTMLAttributes: { class: 'max-w-full rounded-lg my-2' } }),
      Link.configure({ openOnClick: true, autolink: true,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer',
          class: 'text-[#0E7490] underline' } }),
      Youtube.configure({ controls: true, nocookie: true, modestBranding: true,
        HTMLAttributes: { class: 'w-full aspect-video rounded-lg my-2' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const ToolBtn = ({ onClick, active, children, title, testId }) => (
    <button type="button" onClick={onClick} title={title}
      data-testid={testId}
      className={`p-1.5 rounded hover:bg-[#E2E8F0] transition-colors ${active ? 'bg-[#0E7490]/15 text-[#0E7490]' : 'text-[#64748B]'}`}>
      {children}
    </button>
  );

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    try {
      const { data } = await lmsApi.upload('images', file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      toast.error('Image upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const insertVideo = () => {
    const url = videoUrl.trim();
    if (!url) return;
    // Tiptap Youtube extension auto-handles YouTube URLs; for everything
    // else we drop in an iframe via insertContent so Vimeo/Loom work too.
    if (/youtu\.?be/i.test(url)) {
      editor.chain().focus().setYoutubeVideo({ src: url }).run();
    } else {
      editor.chain().focus().insertContent(
        `<div class="my-3"><iframe src="${url}" class="w-full aspect-video rounded-lg" allowfullscreen frameborder="0"></iframe></div>`
      ).run();
    }
    setShowVideoDialog(false);
    setVideoUrl('');
  };

  const insertLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  const insertHtml = () => {
    if (!rawHtml.trim()) return;
    editor.chain().focus().insertContent(rawHtml).run();
    setShowHtmlDialog(false);
    setRawHtml('');
  };

  return (
    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden bg-white" data-testid="rich-content-editor">
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-[#F8FAFC] border-b border-[#E2E8F0] flex-wrap">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" testId="rce-bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" testId="rce-italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline" testId="rce-underline"><UnderlineIcon className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testId="rce-strike"><span className="text-xs line-through font-bold">S</span></ToolBtn>
        <div className="w-px h-4 bg-[#E2E8F0] mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1" testId="rce-h1"><Heading1 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2" testId="rce-h2"><Heading2 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote" testId="rce-quote"><Quote className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block" testId="rce-code"><Code2 className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-4 bg-[#E2E8F0] mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List" testId="rce-bullet"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List" testId="rce-ordered"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        <div className="w-px h-4 bg-[#E2E8F0] mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left" testId="rce-left"><AlignLeft className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center" testId="rce-center"><AlignCenter className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right" testId="rce-right"><AlignRight className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify" testId="rce-justify"><AlignJustify className="w-3.5 h-3.5" /></ToolBtn>
        {enableMedia && (
          <>
            <div className="w-px h-4 bg-[#E2E8F0] mx-1" />
            <label className="p-1.5 rounded hover:bg-[#E2E8F0] text-[#64748B] cursor-pointer" title="Insert image" data-testid="rce-image-btn">
              <ImageIcon className="w-3.5 h-3.5" />
              <input type="file" accept="image/*" className="hidden" onChange={handleImagePick} data-testid="rce-image-input" />
            </label>
            <ToolBtn onClick={() => setShowVideoDialog(true)} title="Insert video (YouTube, Vimeo, Loom…)" testId="rce-video-btn"><Video className="w-3.5 h-3.5" /></ToolBtn>
            <ToolBtn onClick={() => { setLinkUrl(editor.getAttributes('link').href || ''); setShowLinkDialog(true); }} active={editor.isActive('link')} title="Add link" testId="rce-link-btn"><LinkIcon className="w-3.5 h-3.5" /></ToolBtn>
            <ToolBtn onClick={() => setShowHtmlDialog(true)} title="Insert raw HTML" testId="rce-html-btn"><span className="text-[10px] font-mono font-bold">HTML</span></ToolBtn>
          </>
        )}
        <div className="ml-auto" />
        <ToolBtn onClick={() => setPreviewMode(!previewMode)} active={previewMode} title={previewMode ? 'Edit' : 'Preview'} testId="rce-preview-btn">
          {previewMode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </ToolBtn>
      </div>

      {previewMode ? (
        <div
          className="prose prose-sm max-w-none px-4 py-3"
          style={{ minHeight }}
          data-testid="rce-preview"
          dangerouslySetInnerHTML={{ __html: editor.getHTML() }}
        />
      ) : (
        <EditorContent
          editor={editor}
          placeholder={placeholder}
          className="prose prose-sm max-w-none px-4 py-3 focus-within:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[100px] [&_.ProseMirror_img]:rounded-lg"
          style={{ minHeight }}
        />
      )}

      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert link</DialogTitle>
            <DialogDescription>Wrap the selected text with a hyperlink.</DialogDescription>
          </DialogHeader>
          <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com" data-testid="rce-link-input" />
          <DialogFooter>
            {editor.isActive('link') && (
              <Button variant="outline" onClick={() => { editor.chain().focus().unsetLink().run(); setShowLinkDialog(false); }} data-testid="rce-link-remove">Remove link</Button>
            )}
            <Button onClick={insertLink} className="careiq-btn-primary" data-testid="rce-link-confirm">Insert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert video</DialogTitle>
            <DialogDescription>Paste a YouTube, Vimeo, or Loom URL — we'll embed it inline.</DialogDescription>
          </DialogHeader>
          <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" data-testid="rce-video-input" />
          <DialogFooter>
            <Button onClick={insertVideo} className="careiq-btn-primary" data-testid="rce-video-confirm">Embed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showHtmlDialog} onOpenChange={setShowHtmlDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Insert raw HTML</DialogTitle>
            <DialogDescription>Paste HTML, iframe embed code, or table markup. It will be inserted at the cursor.</DialogDescription>
          </DialogHeader>
          <Textarea value={rawHtml} onChange={(e) => setRawHtml(e.target.value)} rows={10} className="font-mono text-xs" placeholder="<div class='callout'>…</div>" data-testid="rce-html-input" />
          <DialogFooter>
            <Button onClick={insertHtml} className="careiq-btn-primary" data-testid="rce-html-confirm">Insert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
